/**
 * Supabase Client + High-Level Helpers für Randori Mail.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY;

const isConfigured =
  !!supabaseUrl &&
  !!supabaseKey &&
  supabaseUrl.includes('.supabase.co') &&
  supabaseKey.length > 20;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const supabase: SupabaseClient<any> | null = isConfigured
  ? createClient(supabaseUrl, supabaseKey, {
      auth: { autoRefreshToken: true, persistSession: true, detectSessionInUrl: true },
    })
  : null;

export const isSupabaseAvailable = isConfigured && supabase !== null;

function requireClient(): SupabaseClient {
  if (!supabase) throw new Error('Supabase nicht konfiguriert — .env prüfen');
  return supabase;
}

/**
 * Anmeldung mit Google. `selectAccount`: Google fragt nach dem Konto, statt
 * still das zuletzt genutzte zu nehmen (Feedback-Link, falsches Konto).
 */
export async function signInWithGoogle(opts?: { selectAccount?: boolean }) {
  const client = requireClient();
  return client.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
      ...(opts?.selectAccount ? { queryParams: { prompt: 'select_account' } } : {}),
    },
  });
}
export async function signOut() { if (!supabase) return; await supabase.auth.signOut(); }
export async function getSession() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * Rollen im Mailtool. geschaeftsfuehrung und studio_leiter kommen seit
 * Migration 0039 von selbst aus dem CRM (Geschäftsführer / Studioleiter) —
 * sie bereiten Mails vor und reichen sie zur Freigabe ein.
 */
export type MailRole = 'admin' | 'redakteur' | 'geschaeftsfuehrung' | 'studio_leiter' | 'viewer';

/** Studios, in denen das Studio (laut DB) als Wert vorkommt — fuer Dropdowns. */
export const STUDIO_OPTIONS = [
  'Charlottenburg', 'Spandau', 'Spandau gGmbH', 'Steglitz',
  'Tegel', 'Mariendorf', 'Waltersdorf', 'Lübars', 'e.V.',
] as const;

export interface MyMailRole {
  role: MailRole;
  full_name: string | null;
  /** mailtool = Eintrag in den Einstellungen, crm = aus der CRM-Rolle */
  quelle: 'mailtool' | 'crm' | null;
  /** Sportschulen der Studioleitung (Schreibweise des Mailtools, z. B. Charlottenburg) */
  studios: string[];
  darf_freigeben: boolean;
  darf_ohne_freigabe: boolean;
}

export async function fetchMyMailRole(): Promise<MyMailRole | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('mail_get_my_role');
  if (error || !data || (data as unknown[]).length === 0) return null;
  const r = (data as Partial<MyMailRole>[])[0];
  return {
    role: r.role as MailRole,
    full_name: r.full_name ?? null,
    quelle: (r.quelle ?? null) as MyMailRole['quelle'],
    studios: r.studios ?? [],
    darf_freigeben: r.darf_freigeben === true,
    darf_ohne_freigabe: r.darf_ohne_freigabe === true,
  };
}
export async function fetchMyPermissions(): Promise<{ can_bypass_approval: boolean }> {
  if (!supabase) return { can_bypass_approval: false };
  const { data } = await supabase.rpc('mail_can_bypass_approval');
  return { can_bypass_approval: Boolean(data) };
}
export async function fetchContactStats(): Promise<{ total: number; optIn: number }> {
  const client = requireClient();
  const { data, error } = await client.rpc('mail_contacts_stats');
  if (error) throw error;
  const row = (data as { total: number; opt_in: number }[] | null)?.[0];
  return { total: Number(row?.total ?? 0), optIn: Number(row?.opt_in ?? 0) };
}
export interface ContactSegmentMembership { contact_id: string; segment_id: string; segment_name: string; is_dynamic: boolean; }
export async function fetchContactSegmentMemberships(): Promise<ContactSegmentMembership[]> {
  const client = requireClient();
  const { data, error } = await client.rpc('mail_contact_segments');
  if (error) throw error;
  return (data ?? []) as ContactSegmentMembership[];
}

export type DesignMode = 'html' | 'template';
export type CampaignStatus = 'draft' | 'pending_approval' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'archived';
export interface ManualRecipient {
  email: string; first_name?: string | null; last_name?: string | null;
  source: 'contact' | 'manual'; has_consent_confirmed: boolean;
}
export interface Campaign {
  id: string; name: string; subject: string; preview_text: string | null;
  original_subject: string | null;
  sender_name: string; sender_email: string; reply_to: string | null;
  status: CampaignStatus;
  html_content: string | null; stripo_html: string | null; stripo_css: string | null;
  editor_design: Record<string, unknown> | null;
  design_mode: DesignMode | null; template_id: string | null;
  include_browser_view: boolean; include_unsubscribe_link: boolean;
  segment_ids: string[]; manual_recipients: ManualRecipient[];
  brevo_campaign_id: number | null;
  scheduled_at: string | null; sent_at: string | null;
  submitted_for_approval_at: string | null;
  approved_at: string | null; rejected_at: string | null;
  rejected_by: string | null; rejection_reason: string | null;
  last_error: string | null; created_by: string | null; approved_by: string | null;
  created_at: string; updated_at: string;
  // Abgleich mit dem Brevo-Entwurf (Migration 0038) — nur in getCampaign geladen
  brevo_content_fingerprint?: string | null;
  brevo_synced_at?: string | null;
  brevo_sync_state?: 'ok' | 'conflict' | 'error' | null;
  brevo_sync_error?: string | null;
  // Status der Kampagne in Brevo, vom Statuslauf gepflegt (Migration 0046)
  brevo_status?: string | null;
  brevo_status_at?: string | null;
  brevo_geplant_fuer?: string | null;
  // Mails von Geschäftsführung und Studioleitung (Migration 0039)
  requires_approval?: boolean;
  review_state?: ReviewState | null;
  review_started_at?: string | null;
  review_started_by?: string | null;
  created_by_name?: string | null;
  created_by_email?: string | null;
  created_by_role?: MailRole | null;
  target_studios?: string[];
  builder_template?: string | null;
  builder_data?: Record<string, unknown> | null;
  request_answers?: RequestAnswers | null;
  desired_send_at?: string | null;
  is_urgent?: boolean;
  urgent_reason?: string | null;
}

export type ReviewState = 'eingereicht' | 'in_pruefung' | 'rueckfrage' | 'freigegeben' | 'abgelehnt';

/** Antworten auf die Vorab-Fragen im Mail-Assistenten. */
export interface RequestAnswers {
  vorlage?: string;
  antworten?: Array<{ id: string; frage: string; antwort: string; hinweis?: string }>;
  /** Was vor dem Einreichen noch geklärt werden muss — leer, sonst lehnt die Datenbank ab. */
  offene_punkte?: string[];
  zielgruppe?: string;
}

const CAMPAIGN_LIST_COLS =
  'id,name,subject,original_subject,preview_text,sender_name,sender_email,reply_to,status,' +
  'design_mode,template_id,include_browser_view,include_unsubscribe_link,' +
  'segment_ids,manual_recipients,brevo_campaign_id,scheduled_at,sent_at,submitted_for_approval_at,' +
  'approved_at,rejected_at,rejected_by,rejection_reason,last_error,created_by,approved_by,' +
  'created_at,updated_at,requires_approval,review_state,review_started_at,review_started_by,' +
  'created_by_name,created_by_email,created_by_role,target_studios,builder_template,' +
  'desired_send_at,is_urgent,urgent_reason,request_answers';
export async function listCampaigns(): Promise<Campaign[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_campaigns').select(CAMPAIGN_LIST_COLS).order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as Campaign[];
}
export async function getCampaign(id: string): Promise<Campaign | null> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_campaigns').select('*').eq('id', id).single();
  if (error) throw error;
  return data as Campaign;
}
export async function createCampaign(payload: Partial<Campaign>): Promise<Campaign> {
  const client = requireClient();
  const { data: { session } } = await client.auth.getSession();
  const { data, error } = await client.from('Mail_campaigns').insert({
    name: payload.name ?? 'Neue Kampagne', subject: payload.subject ?? '',
    sender_name: payload.sender_name ?? '', sender_email: payload.sender_email ?? '',
    preview_text: payload.preview_text ?? null,
    stripo_html: payload.stripo_html ?? null, html_content: payload.html_content ?? null,
    editor_design: payload.editor_design ?? null, design_mode: payload.design_mode ?? null,
    template_id: payload.template_id ?? null,
    include_browser_view: payload.include_browser_view ?? true,
    include_unsubscribe_link: payload.include_unsubscribe_link ?? true,
    manual_recipients: payload.manual_recipients ?? [],
    status: 'draft', created_by: session?.user.id ?? null,
  }).select().single();
  if (error) throw error;
  return data as Campaign;
}
export async function updateCampaign(id: string, updates: Partial<Campaign>): Promise<Campaign> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_campaigns').update(updates).eq('id', id).select().single();
  if (error) throw error;
  return data as Campaign;
}
export async function deleteCampaign(id: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_campaigns').delete().eq('id', id);
  if (error) throw error;
}
/**
 * Zur Freigabe einreichen. Das Einreichen macht die Datenbank
 * (mail_freigabe_schritt): Sie prüft Pflichtangaben und Vorab-Fragen. Die
 * Funktion benachrichtigt danach die Redaktion. Ein Ausweichen auf direktes
 * Schreiben gibt es nicht mehr — den Stand setzt nur der Ablauf.
 */
export async function submitCampaignForApproval(id: string, message?: string): Promise<Campaign> {
  await invokeFunction('notify-approval-needed', { campaign_id: id, message: message?.trim() || undefined });
  const updated = await getCampaign(id);
  if (!updated) throw new Error('Kampagne nicht gefunden');
  return updated;
}

export type FreigabeAktion = 'einreichen' | 'zurueckziehen' | 'in_pruefung' | 'rueckfrage' | 'freigeben' | 'ablehnen';

/** Ein Schritt im Freigabe-Ablauf (Rolle und Stand prüft die Datenbank). */
export async function freigabeSchritt(
  id: string, aktion: FreigabeAktion, grund?: string | null, erwartet?: string | null,
): Promise<{ status: string; review_state: ReviewState | null; action: string }> {
  const client = requireClient();
  const { data, error } = await client.rpc('mail_freigabe_schritt', {
    p_campaign_id: id, p_aktion: aktion, p_grund: grund ?? null, p_erwartet: erwartet ?? null,
  });
  if (error) throw new Error(error.message);
  return data as { status: string; review_state: ReviewState | null; action: string };
}

/** Rückfrage stellen (die Kampagne geht als Entwurf zurück an die Person). */
export async function rejectCampaign(id: string, reason: string): Promise<Campaign> {
  await freigabeSchritt(id, 'rueckfrage', reason);
  await edgeFunctions.notifyReviewDecision(id).catch((e) => console.warn('Benachrichtigung fehlgeschlagen:', e));
  const updated = await getCampaign(id);
  if (!updated) throw new Error('Kampagne nicht gefunden');
  return updated;
}

export interface HistoryEntry {
  id: number; campaign_id: string; created_at: string;
  actor: string | null; actor_name: string | null;
  action: string; from_status: string | null; to_status: string | null; message: string | null;
}
export async function listCampaignHistory(campaignId: string): Promise<HistoryEntry[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_campaign_history')
    .select('*').eq('campaign_id', campaignId).order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as HistoryEntry[];
}
/** Letzte Einträge über alle Mails (Freigaben → Protokoll). */
export async function listRecentHistory(limit = 200): Promise<HistoryEntry[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_campaign_history')
    .select('*').order('created_at', { ascending: false }).limit(limit);
  if (error) throw error;
  return (data ?? []) as HistoryEntry[];
}

/** Eigene Mails (Geschäftsführung, Studioleitung — und alle, die den Assistenten nutzen). */
export async function listMyCampaigns(): Promise<Campaign[]> {
  const client = requireClient();
  const { data: { session } } = await client.auth.getSession();
  if (!session) return [];
  const { data, error } = await client.from('Mail_campaigns').select(CAMPAIGN_LIST_COLS)
    .eq('created_by', session.user.id).order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as Campaign[];
}

/** Alles, was im Freigabe-Ablauf steckt oder dort entschieden wurde. */
export async function listReviewCampaigns(): Promise<Campaign[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_campaigns').select(CAMPAIGN_LIST_COLS)
    .or('status.eq.pending_approval,review_state.not.is.null,requires_approval.eq.true')
    .order('updated_at', { ascending: false }).limit(300);
  if (error) throw error;
  return (data ?? []) as unknown as Campaign[];
}

export async function countOpenReviews(): Promise<number> {
  const client = requireClient();
  const { count, error } = await client.from('Mail_campaigns')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'pending_approval')
    .or('review_state.is.null,review_state.in.(eingereicht,in_pruefung)');
  if (error) return 0;
  return count ?? 0;
}

export interface RedaktionPerson { user_id: string; name: string; email: string; rolle: string }
export async function listRedaktion(): Promise<RedaktionPerson[]> {
  const client = requireClient();
  const { data, error } = await client.rpc('mail_redaktion_liste');
  if (error) throw error;
  return (data ?? []) as RedaktionPerson[];
}

export interface UserSearchHit { user_id: string; email: string; name: string | null; crm_rolle: string | null; mail_rolle: MailRole | null }
export async function searchUsers(q: string): Promise<UserSearchHit[]> {
  const client = requireClient();
  const { data, error } = await client.rpc('mail_benutzer_suchen', { p_suche: q });
  if (error) throw error;
  return (data ?? []) as UserSearchHit[];
}

// Erklärkästen und Einführung — gemerkt je Person (Mail_ui_settings).
export async function fetchTutorialMode(): Promise<boolean> {
  if (!supabase) return true;
  const { data, error } = await supabase.rpc('mail_my_tutorial_mode');
  if (error) throw error;
  return data !== false;
}
export async function setTutorialMode(an: boolean): Promise<void> {
  const client = requireClient();
  const { error } = await client.rpc('mail_set_my_tutorial_mode', { p_an: an });
  if (error) throw error;
}
export async function fetchIntroVersion(): Promise<number> {
  if (!supabase) return 0;
  const { data, error } = await supabase.rpc('mail_my_intro_version');
  if (error) throw error;
  return Number(data ?? 0);
}
export async function setIntroVersion(version: number): Promise<void> {
  const client = requireClient();
  const { error } = await client.rpc('mail_set_my_intro_version', { p_version: version });
  if (error) throw error;
}

export interface Segment {
  id: string; name: string; description: string | null;
  filter_query: Record<string, unknown> | null; contact_count: number;
  brevo_list_id: number | null; is_dynamic: boolean;
  // Brevo-Sync (Migration 0014)
  brevo_sync_enabled: boolean;
  brevo_last_synced_at: string | null;
  brevo_sync_error: string | null;
  // Rollen + Lebenszyklus (Migration 0021)
  studio_scope: string[] | null;
  admin_only: boolean;
  valid_until: string | null;
  is_favorite: boolean;
  // Interner Kopie-Verteiler: bei jedem Versand automatisch dabei (Migration 0035)
  always_included: boolean;
  // Gruppe aus einem CRM-Tag, stündlich gepflegt (Migration 0039)
  crm_tag_slug?: string | null;
  crm_tag_studio?: string | null;
  created_by: string | null; created_at: string; updated_at: string;
}

/** True wenn die Gruppe heute fuer den Versand genutzt werden kann. */
export function isSegmentUsable(s: Segment, today: Date = new Date()): boolean {
  if (!s.valid_until) return true;
  const expiry = new Date(s.valid_until + 'T23:59:59');
  return expiry.getTime() >= today.getTime();
}
export async function listSegments(): Promise<Segment[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_segments').select('*').order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Segment[];
}
export async function getSegment(id: string): Promise<Segment | null> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_segments').select('*').eq('id', id).single();
  if (error) throw error;
  return data as Segment;
}
export async function createSegment(payload: Partial<Segment>): Promise<Segment> {
  const client = requireClient();
  const { data: { session } } = await client.auth.getSession();
  const { data, error } = await client.from('Mail_segments').insert({
    name: payload.name ?? 'Neues Segment', description: payload.description ?? null,
    filter_query: payload.filter_query ?? null, is_dynamic: payload.is_dynamic ?? true,
    studio_scope: payload.studio_scope ?? null,
    admin_only:   payload.admin_only ?? false,
    always_included: payload.always_included ?? false,
    valid_until:  payload.valid_until ?? null,
    created_by: session?.user.id ?? null,
  }).select().single();
  if (error) throw error;
  return data as Segment;
}
export async function updateSegment(id: string, updates: Partial<Segment>): Promise<Segment> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_segments').update(updates).eq('id', id).select().single();
  if (error) throw error;
  return data as Segment;
}
export async function deleteSegment(id: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_segments').delete().eq('id', id);
  if (error) throw error;
}
export async function listSegmentMembers(segment_id: string): Promise<string[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_segment_members').select('contact_id').eq('segment_id', segment_id);
  if (error) throw error;
  return (data ?? []).map((r: { contact_id: string }) => r.contact_id);
}
export async function setSegmentMembers(segment_id: string, contact_ids: string[]): Promise<void> {
  const client = requireClient();
  const { error: delError } = await client.from('Mail_segment_members').delete().eq('segment_id', segment_id);
  if (delError) throw delError;
  if (contact_ids.length > 0) {
    const rows = contact_ids.map((contact_id) => ({ segment_id, contact_id }));
    const { error: insError } = await client.from('Mail_segment_members').insert(rows);
    if (insError) throw insError;
  }
  await client.from('Mail_segments').update({ contact_count: contact_ids.length }).eq('id', segment_id);
}

/**
 * Haus-Vorlagen (aus dem Mailtool erzeugt) tragen ihren Schlüssel und die
 * Emoji-Stufen „ohne" und „viele"; die Stufe „normal" steht in html_content.
 */
export interface TemplateVarianten { haus?: string; ohne?: string; viele?: string }
export interface Template {
  id: string; name: string; description: string | null; category: string | null;
  stripo_html: string; stripo_css: string;
  html_content: string | null; editor_design: Record<string, unknown> | null;
  thumbnail_url: string | null; created_by: string | null; is_global: boolean;
  created_at: string; updated_at: string;
  varianten?: TemplateVarianten | null;
  /** Schlüssel der Haus-Vorlage (in Listen aus varianten->>haus) */
  haus?: string | null;
}
const TEMPLATE_LIST_COLS = 'id,name,description,category,thumbnail_url,is_global,created_by,created_at,updated_at,haus:varianten->>haus';
export async function listTemplates(): Promise<Template[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_templates').select(TEMPLATE_LIST_COLS).order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as Template[];
}
/** Wie listTemplates, aber mit HTML und Emoji-Stufen — für Galerie und Vorlagenwahl. */
export async function listTemplatesMitInhalt(): Promise<Template[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_templates')
    .select(`${TEMPLATE_LIST_COLS},html_content,stripo_html,varianten`)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as Template[];
}
/** HTML einer Vorlage in der gewünschten Emoji-Stufe (fällt auf „normal" zurück). */
export function templateHtml(t: Pick<Template, 'html_content' | 'stripo_html' | 'varianten'>, stufe: 'ohne' | 'normal' | 'viele' = 'normal'): string {
  const v = stufe === 'normal' ? undefined : t.varianten?.[stufe];
  return v || t.html_content || t.stripo_html || '';
}
export interface HausVorlageZeile {
  haus: string; name: string; description: string; category: string;
  normal: string; ohne: string; viele: string;
}
/**
 * Haus-Vorlagen neu schreiben: vorhandene Zeilen (gleicher Schlüssel) werden
 * überschrieben, fehlende angelegt. Eigene Vorlagen bleiben unberührt.
 */
export async function hausVorlagenSchreiben(zeilen: HausVorlageZeile[]): Promise<{ neu: number; aktualisiert: number }> {
  const client = requireClient();
  const { data: vorhanden, error } = await client.from('Mail_templates').select('id,haus:varianten->>haus');
  if (error) throw error;
  const idVon = new Map(((vorhanden ?? []) as unknown as { id: string; haus: string | null }[])
    .filter((r) => r.haus).map((r) => [r.haus as string, r.id]));
  const { data: { session } } = await client.auth.getSession();
  let neu = 0; let aktualisiert = 0;
  for (const z of zeilen) {
    const werte = {
      name: z.name, description: z.description, category: z.category,
      html_content: z.normal, stripo_html: z.normal, stripo_css: '', editor_design: null,
      thumbnail_url: null, is_global: true,
      varianten: { haus: z.haus, ohne: z.ohne, viele: z.viele },
    };
    const id = idVon.get(z.haus);
    const { error: e } = id
      ? await client.from('Mail_templates').update(werte).eq('id', id)
      : await client.from('Mail_templates').insert({ ...werte, created_by: session?.user.id ?? null });
    if (e) throw e;
    if (id) aktualisiert++; else neu++;
  }
  return { neu, aktualisiert };
}
export async function getTemplate(id: string): Promise<Template | null> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_templates').select('*').eq('id', id).single();
  if (error) throw error;
  return data as Template;
}
export async function createTemplate(payload: Partial<Template>): Promise<Template> {
  const client = requireClient();
  const { data: { session } } = await client.auth.getSession();
  const { data, error } = await client.from('Mail_templates').insert({
    name: payload.name ?? 'Neues Template', description: payload.description ?? null,
    category: payload.category ?? null,
    stripo_html: payload.stripo_html ?? '', stripo_css: payload.stripo_css ?? '',
    html_content: payload.html_content ?? null, editor_design: payload.editor_design ?? null,
    thumbnail_url: payload.thumbnail_url ?? null, is_global: payload.is_global ?? true,
    created_by: session?.user.id ?? null,
  }).select().single();
  if (error) throw error;
  return data as Template;
}
export async function updateTemplate(id: string, updates: Partial<Template>): Promise<Template> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_templates').update(updates).eq('id', id).select().single();
  if (error) throw error;
  return data as Template;
}
export async function deleteTemplate(id: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_templates').delete().eq('id', id);
  if (error) throw error;
}
export async function uploadTemplateThumbnail(templateId: string, file: File): Promise<string> {
  const client = requireClient();
  const ext = file.name.split('.').pop() || 'png';
  const path = `thumbnails/${templateId}-${Date.now()}.${ext}`;
  const { error } = await client.storage.from('email-templates').upload(path, file, { cacheControl: '3600', upsert: true });
  if (error) throw error;
  const { data } = client.storage.from('email-templates').getPublicUrl(path);
  return data.publicUrl;
}

export interface MediaItem {
  id: string; filename: string; url: string; storage_path: string;
  content_type: string | null; size_bytes: number | null;
  width: number | null; height: number | null;
  alt_text: string | null; folder: string;
  uploaded_by: string | null; created_at: string;
}
export async function listMedia(folder = ''): Promise<MediaItem[]> {
  const client = requireClient();
  let q = client.from('Mail_media').select('*').order('created_at', { ascending: false });
  if (folder) q = q.eq('folder', folder);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as MediaItem[];
}
export async function uploadMedia(file: File, folder = 'general'): Promise<MediaItem> {
  const client = requireClient();
  const { data: { session } } = await client.auth.getSession();
  const safeName = file.name.replace(/[^\w.-]+/g, '_');
  const path = `media/${folder}/${Date.now()}-${safeName}`;
  const { error: upErr } = await client.storage.from('email-assets').upload(path, file, { cacheControl: '31536000', contentType: file.type || undefined, upsert: false });
  if (upErr) throw upErr;
  const { data: urlData } = client.storage.from('email-assets').getPublicUrl(path);
  const url = urlData.publicUrl;
  let width: number | null = null; let height: number | null = null;
  if (file.type.startsWith('image/')) {
    try {
      const dim = await new Promise<{ w: number; h: number }>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
      width = dim.w; height = dim.h;
    } catch { /* ignore */ }
  }
  const { data, error } = await client.from('Mail_media').insert({
    filename: file.name, url, storage_path: path,
    content_type: file.type || null, size_bytes: file.size,
    width, height, folder, uploaded_by: session?.user.id ?? null,
  }).select().single();
  if (error) throw error;
  return data as MediaItem;
}
export async function deleteMedia(item: MediaItem): Promise<void> {
  const client = requireClient();
  await client.storage.from('email-assets').remove([item.storage_path]).catch(() => {});
  const { error } = await client.from('Mail_media').delete().eq('id', item.id);
  if (error) throw error;
}
export async function updateMediaAltText(id: string, alt_text: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_media').update({ alt_text }).eq('id', id);
  if (error) throw error;
}

// ==========================================================================
// Contacts
// ==========================================================================
export interface Contact {
  id: string; crm_id: string | null; email: string;
  first_name: string | null; last_name: string | null;
  school: string | null; organization: string | null;
  member_status: string | null; customer_status: string | null;
  is_active: boolean; is_member: boolean;
  birth_date: string | null; gender: string | null;
  is_trainer: boolean; role: string | null;
  opt_in: boolean; opt_in_date: string | null;
  unsubscribed_at: string | null; bounce_status: string | null;
  brevo_contact_id: number | null; tags: string[];
  // Aggregat aller CRM-Personen, die sich diese E-Mail teilen (Familien-Postfaecher).
  // Bei rein manuellen Kontakten ist household_member_count = 0.
  household_member_count: number;
  household_member_names: string[];
  household_organizations: string[];
  household_is_member: boolean;
  household_has_trainer: boolean;
  // Geburtsdatum des juengsten aktiven Mitglieds im Haushalt (fuer Haushalts-Altersfilter).
  household_youngest_member_birth_date: string | null;
  last_synced_at: string; source_updated_at: string | null;
  created_at: string; updated_at: string;
}

export interface ContactFilters {
  search?: string;
  organization?: string;
  isMember?: 'all' | 'yes' | 'no';
  optIn?: 'all' | 'yes' | 'no' | 'unsubscribed';
  bounceStatus?: 'all' | 'none' | 'soft' | 'hard';
  tag?: string;
}

export interface ContactListResult {
  contacts: Contact[];
  // gefilterte Zaehlung (fuer Pagination und "X von Y Treffer").
  filteredTotal: number;
  // globale Zaehlungen (Haushalt-aware, fuer KPI-Kacheln). Nicht vom Filter beeinflusst.
  totalEmails: number;
  totalPersons: number;
  membersEmails: number;        // Postfaecher mit aktivem Haushaltsmitglied
  membersPersons: number;       // echte Personen in Mitgliedshaushalten
  optInEmails: number;          // Postfaecher versendbar (opt_in AND NOT unsubscribed)
  // legacy alias - bleibt fuer alte Callsites, identisch mit filteredTotal
  total: number;
  optIn: number;
  members: number;
}

export type ContactSortField = 'email' | 'first_name' | 'last_name' | 'organization' | 'is_member' | 'opt_in' | 'updated_at';

export async function listContacts(
  filters: ContactFilters | string = {},
  limit = 50,
  offset = 0,
  sortBy: ContactSortField = 'updated_at',
  sortDir: 'asc' | 'desc' = 'desc',
): Promise<ContactListResult> {
  const f: ContactFilters = typeof filters === 'string' ? { search: filters } : filters;
  const client = requireClient();

  let query = client.from('Mail_contacts').select('*', { count: 'exact' })
    .order(sortBy, { ascending: sortDir === 'asc' })
    .range(offset, offset + limit - 1);

  if (f.search?.trim()) {
    const s = f.search.trim();
    // Auch in household_member_names_text suchen, damit "Emilian" das Postfach
    // von Raphael findet, in dem Emilian als Familienmitglied steckt.
    query = query.or(`email.ilike.%${s}%,first_name.ilike.%${s}%,last_name.ilike.%${s}%,household_member_names_text.ilike.%${s}%`);
  }
  if (f.organization) query = query.eq('organization', f.organization);
  if (f.isMember === 'yes') query = query.eq('is_member', true);
  if (f.isMember === 'no')  query = query.eq('is_member', false);
  if (f.optIn === 'yes')               query = query.eq('opt_in', true).is('unsubscribed_at', null);
  else if (f.optIn === 'no')           query = query.eq('opt_in', false).is('unsubscribed_at', null);
  else if (f.optIn === 'unsubscribed') query = query.not('unsubscribed_at', 'is', null);
  if (f.bounceStatus === 'none')      query = query.is('bounce_status', null);
  else if (f.bounceStatus === 'soft') query = query.eq('bounce_status', 'soft');
  else if (f.bounceStatus === 'hard') query = query.eq('bounce_status', 'hard');
  if (f.tag?.trim()) query = query.contains('tags', [f.tag.trim()]);

  const { data, count, error } = await query;
  if (error) throw error;

  // Globale Statistiken via RPC (Haushalt-aware). RPC RETURNS TABLE -> Array mit 1 Zeile.
  const { data: globalsArr } = await client.rpc('mail_contacts_global_stats');
  const g = (Array.isArray(globalsArr) ? (globalsArr[0] ?? {}) : (globalsArr ?? {})) as {
    emails_total?: number;     persons_total?: number;
    members_emails?: number;   members_persons?: number;
    optin_emails?: number;
  };

  const filteredTotal = count ?? 0;
  return {
    contacts: (data ?? []) as Contact[],
    filteredTotal,
    totalEmails:    g.emails_total    ?? 0,
    totalPersons:   g.persons_total   ?? 0,
    membersEmails:  g.members_emails  ?? 0,
    membersPersons: g.members_persons ?? 0,
    optInEmails:    g.optin_emails    ?? 0,
    // legacy aliases
    total:   filteredTotal,
    optIn:   g.optin_emails    ?? 0,
    members: g.members_emails  ?? 0,
  };
}

export async function listAllContactsForPicker(): Promise<Contact[]> {
  const client = requireClient();
  // PostgREST cappt per Default bei db-max-rows (typisch 1000); .limit() hebt das
  // nicht auf. Daher paginieren wir mit .range() bis die Seite kuerzer als PAGE ist.
  const PAGE = 1000;
  const out: Contact[] = [];
  let from = 0;
  while (true) {
    const { data, error } = await client.from('Mail_contacts')
      .select('id, email, first_name, last_name, school, organization, member_status, customer_status, is_active, is_member, opt_in, unsubscribed_at, bounce_status, household_member_count, household_member_names, household_organizations, household_is_member, household_has_trainer')
      .order('updated_at', { ascending: false })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data ?? []) as Contact[];
    out.push(...rows);
    if (rows.length < PAGE) break;
    from += PAGE;
  }
  return out;
}
export async function createContact(payload: Partial<Contact>): Promise<Contact> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_contacts').insert({
    email: payload.email,
    first_name: payload.first_name ?? null, last_name: payload.last_name ?? null,
    school: payload.school ?? null, organization: payload.organization ?? null,
    member_status: payload.member_status ?? null,
    opt_in: payload.opt_in ?? false,
    opt_in_date: payload.opt_in ? (payload.opt_in_date ?? new Date().toISOString()) : null,
    tags: payload.tags ?? [],
  }).select().single();
  if (error) throw error;
  return data as Contact;
}
export async function updateContact(id: string, updates: Partial<Contact>): Promise<Contact> {
  const client = requireClient();
  const patch: Partial<Contact> = { ...updates };
  if (updates.opt_in === true && !updates.opt_in_date) patch.opt_in_date = new Date().toISOString();
  const { data, error } = await client.from('Mail_contacts').update(patch).eq('id', id).select().single();
  if (error) throw error;
  return data as Contact;
}
export async function deleteContact(id: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_contacts').delete().eq('id', id);
  if (error) throw error;
}

export interface Sender {
  id: string; name: string; email: string;
  brevo_sender_id: number | null;
  is_default: boolean; is_verified: boolean;
  created_at: string;
}
export async function listSenders(): Promise<Sender[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_senders').select('*').order('is_default', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Sender[];
}
export async function createSender(payload: Partial<Sender>): Promise<Sender> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_senders').insert({
    name: payload.name ?? '', email: payload.email ?? '',
    is_default: payload.is_default ?? false,
  }).select().single();
  if (error) throw error;
  return data as Sender;
}
export async function deleteSender(id: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_senders').delete().eq('id', id);
  if (error) throw error;
}

export interface MailUserRole {
  user_id: string; role: MailRole; full_name: string | null;
  can_send_without_approval: boolean;
  /** Freigaberecht (Redaktion) — Admins haben es immer. */
  can_approve: boolean;
  studios: string[];
  created_at: string; updated_at: string;
}
export async function setUserCanApprove(user_id: string, canApprove: boolean): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_user_roles').update({ can_approve: canApprove }).eq('user_id', user_id);
  if (error) throw error;
}

/** Felder, die der Mail-Assistent schreibt. */
export type AssistentEntwurf = Pick<Campaign,
  'name' | 'subject' | 'preview_text' | 'sender_name' | 'sender_email' | 'reply_to' |
  'html_content' | 'segment_ids'> & {
  builder_template: string;
  builder_data: Record<string, unknown>;
  request_answers: RequestAnswers;
  desired_send_at: string | null;
  is_urgent: boolean;
  urgent_reason: string | null;
  target_studios: string[];
};

/**
 * Entwurf aus dem Assistenten anlegen oder aktualisieren. Stand, Freigabe
 * und Herkunft setzt die Datenbank selbst (Trigger mail_campaigns_schutz).
 */
export async function saveAssistentEntwurf(id: string | null, e: AssistentEntwurf): Promise<Campaign> {
  const client = requireClient();
  const felder = {
    name: e.name, subject: e.subject, preview_text: e.preview_text ?? null,
    sender_name: e.sender_name, sender_email: e.sender_email, reply_to: e.reply_to ?? null,
    html_content: e.html_content, stripo_html: e.html_content, editor_design: null,
    design_mode: 'html' as const, template_id: null,
    include_browser_view: true, include_unsubscribe_link: true,
    segment_ids: e.segment_ids ?? [], manual_recipients: [],
    builder_template: e.builder_template, builder_data: e.builder_data,
    request_answers: e.request_answers, desired_send_at: e.desired_send_at,
    is_urgent: e.is_urgent, urgent_reason: e.urgent_reason, target_studios: e.target_studios,
  };
  if (id) {
    const { data, error } = await client.from('Mail_campaigns').update(felder).eq('id', id).select().single();
    if (error) throw error;
    return data as Campaign;
  }
  const { data: { session } } = await client.auth.getSession();
  const { data, error } = await client.from('Mail_campaigns')
    .insert({ ...felder, status: 'draft', created_by: session?.user.id ?? null })
    .select().single();
  if (error) throw error;
  return data as Campaign;
}
/** Eine Zeile der Übersicht „Benutzer & Rechte" (nur für Admins, Migration 0042). */
export interface BenutzerZeile {
  user_id: string;
  email: string | null;
  name: string | null;
  rolle: MailRole;
  /** mailtool = Eintrag in den Einstellungen, crm = aus der CRM-Rolle */
  quelle: 'mailtool' | 'crm';
  /** Rolle laut CRM (Geschäftsführer/Studioleiter), auch wenn ein Eintrag im Mailtool vorgeht */
  crm_rolle: 'geschaeftsfuehrung' | 'studio_leiter' | null;
  studios: string[];
  darf_freigeben: boolean;
  darf_ohne_freigabe: boolean;
  letzte_anmeldung: string | null;
  eintrag_seit: string | null;
  /** Eintrag im Mailtool mit eigenen Sportschulen (statt der aus dem CRM) */
  eigene_studios: boolean;
  mails: number;
  mails_offen: number;
}
export async function listBenutzerUebersicht(): Promise<BenutzerZeile[]> {
  const client = requireClient();
  const { data, error } = await client.rpc('mail_benutzer_uebersicht');
  if (error) throw new Error(error.message);
  return (data ?? []) as BenutzerZeile[];
}

/**
 * Rechte einer Person setzen. Kommt die Rolle bisher aus dem CRM, entsteht dafür
 * ein Eintrag im Mailtool mit derselben Rolle (bei der Studioleitung bleiben die
 * Sportschulen aus dem CRM). Ist danach nichts Besonderes mehr eingetragen,
 * wird der Eintrag wieder entfernt — dann gilt die CRM-Rolle wie zuvor.
 */
export async function setzeRechte(z: BenutzerZeile, teil: { rolle?: MailRole; freigeben?: boolean; ohneFreigabe?: boolean }): Promise<void> {
  const client = requireClient();
  const rolle = teil.rolle ?? z.rolle;
  const canApprove = rolle === 'redakteur' ? (teil.freigeben ?? z.darf_freigeben) : false;
  const ohne = rolle === 'admin' || rolle === 'viewer' ? false : (teil.ohneFreigabe ?? (z.rolle === 'admin' ? false : z.darf_ohne_freigabe));
  const wieImCrm = !!z.crm_rolle && rolle === z.crm_rolle && !ohne && !canApprove && !z.eigene_studios;
  if (wieImCrm) {
    if (z.quelle === 'mailtool') {
      const { error } = await client.from('Mail_user_roles').delete().eq('user_id', z.user_id);
      if (error) throw error;
    }
    return;
  }
  const { error } = await client.from('Mail_user_roles').upsert({
    user_id: z.user_id,
    role: rolle,
    full_name: z.name,
    // Studioleitung aus dem CRM: leere Liste = Sportschulen weiter aus dem CRM
    ...(z.quelle === 'crm' ? { studios: [] } : {}),
    can_approve: canApprove,
    can_send_without_approval: ohne,
  }, { onConflict: 'user_id' });
  if (error) throw error;
}

export async function listMailUsers(): Promise<MailUserRole[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_user_roles').select('*').order('created_at');
  if (error) throw error;
  return (data ?? []) as MailUserRole[];
}
export async function upsertMailUserRole(user_id: string, role: MailRole, full_name?: string, studios?: string[]): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_user_roles').upsert({
    user_id,
    role,
    full_name: full_name ?? null,
    studios:   studios ?? [],
  }, { onConflict: 'user_id' });
  if (error) throw error;
}
export async function updateMailUserStudios(user_id: string, studios: string[]): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_user_roles').update({ studios }).eq('user_id', user_id);
  if (error) throw error;
}
/** Liefert die Studios des aktuell eingeloggten Users (leer fuer Admin/Redakteur — die sehen alles). */
export async function fetchMyStudios(): Promise<string[]> {
  if (!supabase) return [];
  const { data } = await supabase.rpc('mail_my_studios');
  return (data as string[]) ?? [];
}
export async function setUserCanBypassApproval(user_id: string, canBypass: boolean): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_user_roles').update({ can_send_without_approval: canBypass }).eq('user_id', user_id);
  if (error) throw error;
}
export async function deleteMailUserRole(user_id: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('Mail_user_roles').delete().eq('user_id', user_id);
  if (error) throw error;
}

export async function getAppSetting<T = unknown>(key: string): Promise<T | null> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_app_settings').select('value').eq('key', key).maybeSingle();
  if (error) throw error;
  return (data?.value ?? null) as T | null;
}
export async function setAppSetting(key: string, value: unknown): Promise<void> {
  const client = requireClient();
  const { data: { session } } = await client.auth.getSession();
  const { error } = await client.from('Mail_app_settings').upsert({
    key, value, updated_by: session?.user.id ?? null, updated_at: new Date().toISOString(),
  }, { onConflict: 'key' });
  if (error) throw error;
}
export async function getRequireApproval(): Promise<boolean> {
  const value = await getAppSetting<boolean>('require_approval');
  return Boolean(value);
}
export async function setRequireApproval(enabled: boolean): Promise<void> {
  await setAppSetting('require_approval', enabled);
}

export interface CampaignStats {
  campaign_id: string;
  sent_count: number; delivered_count: number;
  opened_count: number; unique_opens: number;
  clicked_count: number; unique_clicks: number;
  soft_bounces: number; hard_bounces: number;
  unsubscribed: number; complaints: number;
  links_stats: Array<{ url: string; clicks: number }> | null;
  last_updated: string;
}
export async function getCampaignStats(campaign_id: string): Promise<CampaignStats | null> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_campaign_stats').select('*').eq('campaign_id', campaign_id).maybeSingle();
  if (error) throw error;
  return (data ?? null) as CampaignStats | null;
}
export interface SyncLogEntry {
  id: string; sync_type: string; status: string;
  records_in: number | null; records_ok: number | null; records_err: number | null;
  error_detail: Record<string, unknown> | null; duration_ms: number | null;
  started_at: string; finished_at: string | null;
}
export async function fetchSyncLog(limit = 20): Promise<SyncLogEntry[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_sync_log').select('*').order('started_at', { ascending: false }).limit(limit);
  if (error) throw error;
  return (data ?? []) as SyncLogEntry[];
}

// ==========================================================================
// Haushalts-Personen (Detail-Modal)
// ==========================================================================

export interface HouseholdPerson {
  membership_id: string;
  customer_id: string | null;
  first_name: string | null;
  last_name: string | null;
  organization: string | null;
  crm_status: string | null;
  role: string | null;
  is_trainer: boolean;
  is_ev_member: boolean | null;
  birth_date: string | null;
  gender: string | null;
  contract_start: string | null;
  contract_end: string | null;
  terminated: boolean;
  termination_date: string | null;
  is_active_member: boolean;
}

/**
 * Liefert alle Personen, die sich diese E-Mail teilen, dedupliziert nach
 * customer_id (eine Person kann mehrere Mitgliedschaften haben — wir nehmen
 * die "beste" je Person: aktiv > zuletzt begonnen).
 */
export async function listHouseholdPersons(email: string): Promise<HouseholdPerson[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_contact_persons')
    .select('membership_id, customer_id, first_name, last_name, organization, crm_status, role, is_trainer, is_ev_member, birth_date, gender, contract_start, contract_end, terminated, termination_date, is_active_member')
    .eq('email', email.toLowerCase());
  if (error) throw error;
  const rows = (data ?? []) as HouseholdPerson[];

  const byCustomer = new Map<string, HouseholdPerson>();
  for (const r of rows) {
    const key = r.customer_id ?? r.membership_id;
    const prev = byCustomer.get(key);
    if (!prev) { byCustomer.set(key, r); continue; }
    const better =
      (r.is_active_member && !prev.is_active_member) ||
      (r.is_active_member === prev.is_active_member && (r.contract_start ?? '') > (prev.contract_start ?? ''));
    if (better) byCustomer.set(key, r);
  }
  return Array.from(byCustomer.values()).sort((a, b) => {
    // Aktive zuerst, dann nach Nachname
    if (a.is_active_member !== b.is_active_member) return a.is_active_member ? -1 : 1;
    return (a.last_name ?? '').localeCompare(b.last_name ?? '');
  });
}

export interface QualitySummary {
  total: number; total_active: number; total_inactive: number;
  any_issue_active: number;
  missing_first_name: number; missing_last_name: number;
  missing_studio: number; missing_birth_date: number; missing_gender: number;
  suspicious_email: number;
  first_name_whitespace: number; last_name_whitespace: number;
  birth_date_unrealistic: number;
  hard_bounced: number; unsubscribed: number;
}
export interface QualityContact {
  id: string; crm_id: string | null; email: string;
  first_name: string | null; last_name: string | null;
  organization: string | null; school: string | null;
  birth_date: string | null; gender: string | null; role: string | null;
  is_active: boolean; opt_in: boolean; is_trainer: boolean;
  unsubscribed_at: string | null; bounce_status: string | null;
  missing_first_name: boolean; missing_last_name: boolean; missing_studio: boolean;
  missing_birth_date: boolean; missing_gender: boolean; suspicious_email: boolean;
  first_name_has_whitespace: boolean; last_name_has_whitespace: boolean;
  birth_date_unrealistic: boolean; hard_bounced: boolean; unsubscribed: boolean;
  has_any_issue: boolean;
}
export interface EmailDupeCluster {
  email: string; distinct_persons: number; membership_rows: number;
  names: string[]; organizations: string[];
  // Wer "gewinnt" aktuell die E-Mail in Mail_contacts (1 Person pro E-Mail).
  winner_name: string | null;
  winner_crm_id: string | null;
  winner_organization: string | null;
}
export interface OrphanMembership {
  membership_id: string; customer_id: string;
  first_name: string | null; last_name: string | null;
  email: string | null; organization: string | null;
  contract_start: string | null;
  reason: 'no_email' | 'invalid_email_syntax' | 'shared_email_other_person';
}
export async function fetchQualitySummary(): Promise<QualitySummary | null> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_quality_summary').select('*').maybeSingle();
  if (error) throw error;
  return (data ?? null) as QualitySummary | null;
}
export async function listQualityContacts(filter: string = 'any_issue', limit = 500): Promise<QualityContact[]> {
  const client = requireClient();
  let q = client.from('Mail_contacts_quality').select('*').eq('is_active', true).order('email').limit(limit);
  if (filter === 'any_issue') q = q.eq('has_any_issue', true);
  else if (filter !== 'all') q = q.eq(filter, true);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as QualityContact[];
}
export async function listEmailDupes(limit = 1000): Promise<EmailDupeCluster[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_email_dupes').select('*').limit(limit);
  if (error) throw error;
  return (data ?? []) as EmailDupeCluster[];
}
export async function listOrphanMemberships(limit = 1000): Promise<OrphanMembership[]> {
  const client = requireClient();
  const { data, error } = await client.from('Mail_orphan_memberships').select('*').limit(limit);
  if (error) throw error;
  return (data ?? []) as OrphanMembership[];
}

/**
 * Empfaenger-Audit: wer kriegt Mails, wer nicht und warum.
 * Zwei Sichtweisen:
 *  - postfach: Postfach-Ebene (1 Zeile pro E-Mail, dedupliziert ueber Familie).
 *  - person:   CRM-Personen-Ebene (jede Mitgliedschaft einzeln, inkl.
 *              Probeschueler die sich Email mit aktivem Mitglied teilen).
 *  - per_studio: pro Studio jeweils mailable + family-only + Pipeline-Status.
 */
export interface AudiencePostboxStats {
  mailable: number;
  mailable_direct_member: number;
  mailable_family_only: number;
  blocked_ex_mitglied: number;
  postbox_with_int_role: number;
  blocked_ex_trainer: number;
  blocked_unsubscribed: number;
  blocked_hard_bounce: number;
  manual_mailable: number;
  manual_blocked: number;
  total_postfach: number;
}
export interface AudiencePersonStats {
  personen_aktiv: number;
  personen_probe_pipeline: number;
  personen_gekuendigt: number;
  personen_sonstige_inaktiv: number;
  total_personen: number;
}
export interface AudienceStudioRow {
  org: string;
  mailable: number;
  davon_family_only: number;
  personen_aktiv: number;
  personen_probe: number;
  personen_gekuendigt: number;
}
export interface AudienceAuditSummary {
  postfach: AudiencePostboxStats;
  person: AudiencePersonStats;
  per_studio: AudienceStudioRow[];
}
export async function fetchAudienceAuditSummary(): Promise<AudienceAuditSummary | null> {
  const client = requireClient();
  const { data, error } = await client.rpc('mail_audience_audit_summary');
  if (error) throw error;
  return (data ?? null) as AudienceAuditSummary | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function invokeFunction<T = unknown>(name: string, body?: any): Promise<T> {
  if (!supabaseUrl || !supabaseKey) throw new Error('Supabase nicht konfiguriert');
  const client = requireClient();
  const { data: { session } } = await client.auth.getSession();
  if (!session) throw new Error('Nicht angemeldet — bitte neu einloggen.');
  const response = await fetch(`${supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      'apikey': supabaseKey,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await response.text();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!response.ok) {
    const msg = data?.error || data?.message || text || `HTTP ${response.status}`;
    throw new Error(msg);
  }
  return data as T;
}
export interface BrevoDraftResult {
  ok: boolean;
  result: 'pushed' | 'pulled' | 'conflict' | 'not_draft' | 'not_open' | 'no_draft' | 'status';
  brevo_status?: string;
  /** bei not_open: Status im Mailtool */
  status?: string;
  mailtool_status?: string | null;
}
export const edgeFunctions = {
  createCampaignInBrevo: (campaign_id: string) => invokeFunction('create-campaign', { campaign_id }),
  // Brevo-Entwurf und Mailtool abgleichen: push = Mailtool-Stand nach Brevo
  // (force: auch wenn dort geaendert; angehalten: auch in eine in Brevo
  // angehaltene Kampagne), pull = Brevo-Stand ins Mailtool.
  brevoDraft: (campaign_id: string, action: 'push' | 'pull' | 'status', force = false, angehalten = false) =>
    invokeFunction<BrevoDraftResult>('brevo-draft', { campaign_id, action, force, angehalten }),
  sendTestEmail: (campaign_id: string, test_emails: string[]) => invokeFunction('send-test-email', { campaign_id, test_emails }),
  // Nach Freigabe, Rückfrage oder Ablehnung die einreichende Person benachrichtigen.
  notifyReviewDecision: (campaign_id: string, schedule_at?: string | null) =>
    invokeFunction<{ ok: boolean; sent_to: number }>('notify-review-decision', { campaign_id, schedule_at: schedule_at ?? null }),
  sendCampaign: (campaign_id: string, schedule_at?: string) => invokeFunction<{ ok: boolean; pending_approval?: boolean; recipient_count?: number }>('send-campaign', { campaign_id, schedule_at }),
  refreshCampaignStats: (campaign_id: string) => invokeFunction('refresh-campaign-stats', { campaign_id }),
  resolveSegment: (segment_id: string) =>
    invokeFunction<{ contact_count: number; contacts: Contact[] }>('resolve-segment', { segment_id }),
  syncSegmentToBrevo: (segment_id: string) =>
    invokeFunction<{ ok: boolean; segment_id: string; list_id: number; total: number; added: number; removed: number }>(
      'sync-segment-to-brevo', { segment_id }
    ),
};
export async function uploadCampaignAsset(campaignId: string, file: File): Promise<string> {
  const client = requireClient();
  const path = `campaigns/${campaignId}/${Date.now()}-${file.name}`;
  const { error } = await client.storage.from('email-assets').upload(path, file, { cacheControl: '31536000', upsert: false });
  if (error) throw error;
  const { data } = client.storage.from('email-assets').getPublicUrl(path);
  return data.publicUrl;
}

// ── Abmeldungen & Bounces ────────────────────────────────────────────────────
// Eigener Reiter (Konrad, 18.08.2026): Wer hat sich abgemeldet, wo kommen
// Mails nicht an. Abmeldungen kommen über den stündlichen Blacklist-Abgleich
// aus Brevo (unsubscribed_at am Kontakt), Kampagnen-Bounces aus der
// Statistik-Aktualisierung (Mail_campaign_stats).

export async function listUnsubscribedContacts(): Promise<Contact[]> {
  const client = requireClient();
  const { data, error } = await client
    .from('Mail_contacts')
    .select('*')
    .not('unsubscribed_at', 'is', null)
    .order('unsubscribed_at', { ascending: false })
    .limit(2000);
  if (error) throw error;
  return (data ?? []) as Contact[];
}

export async function listBouncedContacts(): Promise<Contact[]> {
  const client = requireClient();
  const { data, error } = await client
    .from('Mail_contacts')
    .select('*')
    .not('bounce_status', 'is', null)
    .order('updated_at', { ascending: false })
    .limit(2000);
  if (error) throw error;
  return (data ?? []) as Contact[];
}

export interface CampaignBounceRow {
  id: string; name: string; subject: string; sent_at: string | null;
  sent_count: number; hard_bounces: number; soft_bounces: number;
  unsubscribed: number; complaints: number;
}

/** Versandte Kampagnen mit ihren Bounce-/Abmelde-Zahlen, neueste zuerst. */
export async function listCampaignBounces(limitCampaigns = 30): Promise<CampaignBounceRow[]> {
  const client = requireClient();
  const { data: campaigns, error } = await client
    .from('Mail_campaigns')
    .select('id,name,subject,sent_at')
    .not('sent_at', 'is', null)
    .order('sent_at', { ascending: false })
    .limit(limitCampaigns);
  if (error) throw error;
  const rows = (campaigns ?? []) as Array<{ id: string; name: string; subject: string; sent_at: string | null }>;
  if (rows.length === 0) return [];
  const { data: stats, error: statErr } = await client
    .from('Mail_campaign_stats')
    .select('campaign_id,sent_count,hard_bounces,soft_bounces,unsubscribed,complaints')
    .in('campaign_id', rows.map(r => r.id));
  if (statErr) throw statErr;
  const byId = new Map((stats ?? []).map(s => [(s as { campaign_id: string }).campaign_id, s as unknown as CampaignStats]));
  return rows.map(r => {
    const s = byId.get(r.id);
    return {
      ...r,
      sent_count: s?.sent_count ?? 0,
      hard_bounces: s?.hard_bounces ?? 0,
      soft_bounces: s?.soft_bounces ?? 0,
      unsubscribed: s?.unsubscribed ?? 0,
      complaints: s?.complaints ?? 0,
    };
  });
}
